import { Award, BookOpen, Compass, Users } from "lucide-react";
import { Link } from "react-router-dom";

const values = [
  { icon: Compass, title: "Rigor", desc: "Methods grounded in peer-reviewed standards." },
  { icon: Users, title: "Equity", desc: "Disparities and SDOH considered by default." },
  { icon: Award, title: "Utility", desc: "Findings designed to drive decisions, not sit on shelves." },
  { icon: BookOpen, title: "Transparency", desc: "Every claim traceable to data." },
];

export default function About() {
  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-24" data-testid="about-page">
      <div className="grid lg:grid-cols-12 gap-10 py-16">
        <div className="lg:col-span-7">
          <div className="overline text-[var(--red)] mb-4">Who we are</div>
          <h1 className="font-display font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            We translate public health programs into <span className="text-[var(--red)]">measurable outcomes</span>.
          </h1>
          <p className="mt-7 text-lg text-[var(--text-2)] leading-relaxed">
            Public Health Masters Evaluation Group LLC is a Nevada-based research and evaluation firm. We combine epidemiology, program evaluation, and data science to give agencies, nonprofits, and health systems the evidence they need to act.
          </p>
        </div>
        <div className="lg:col-span-5">
          <img
            src="https://images.unsplash.com/photo-1758518731468-98e90ffd7430?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBkaXZlcnNlJTIwdGVhbSUyMG1lZXRpbmclMjBtb2Rlcm4lMjBvZmZpY2V8ZW58MHx8fHwxNzc5NTc4ODk1fDA&ixlib=rb-4.1.0&q=85"
            alt="Team"
            className="w-full max-h-[80vh] object-cover border border-[var(--border)]"
          />
        </div>
      </div>

      <section className="grid lg:grid-cols-2 gap-12 py-20 border-t border-[var(--border)]">
        <div>
          <div className="overline text-[var(--silver)] mb-3">Our mission</div>
          <h2 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight">
            Evidence that builds healthier communities.
          </h2>
        </div>
        <div className="text-[var(--text-2)] leading-relaxed space-y-4">
          <p>We exist because too many programs collect data without ever learning from it. We work alongside teams to design evaluation frameworks that respect their mission and their constraints — and produce findings funders, boards, and frontline staff can actually use.</p>
          <p>Every engagement starts with the question: <span className="text-white font-medium">what decision are we trying to inform?</span> From there we build backwards — into instruments, indicators, and analyses purpose-built for that decision.</p>
        </div>
      </section>

      <section className="py-20 border-t border-[var(--border)]">
        <div className="overline text-[var(--red)] mb-4">What guides us</div>
        <h2 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight mb-12">Four principles in every engagement.</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--border)]">
          {values.map((v) => (
            <div key={v.title} className="bg-[var(--bg)] p-8" data-testid={`value-${v.title.toLowerCase()}`}>
              <v.icon className="w-6 h-6 text-[var(--red)] mb-5" />
              <h3 className="font-display text-lg font-semibold mb-2">{v.title}</h3>
              <p className="text-sm text-[var(--text-2)]">{v.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-20 border-t border-[var(--border)] grid lg:grid-cols-2 gap-12 items-center">
        <img
          src="https://images.unsplash.com/photo-1758873271761-6cfe9b4f000c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHw0fHxwcm9mZXNzaW9uYWwlMjBkaXZlcnNlJTIwdGVhbSUyMG1lZXRpbmclMjBtb2Rlcm4lMjBvZmZpY2V8ZW58MHx8fHwxNzc5NTc4ODk1fDA&ixlib=rb-4.1.0&q=85"
          alt="Collaboration"
          className="w-full max-h-[70vh] object-cover border border-[var(--border)]"
        />
        <div>
          <div className="overline text-[var(--silver)] mb-3">Leadership</div>
          <h2 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight mb-4">A bench built for serious work.</h2>
          <p className="text-[var(--text-2)] leading-relaxed mb-6">
            Public Health Masters Evaluation Group LLC is led by <span className="text-white font-medium">George Menzor, B.S., MPH</span>, who has deep passions and appreciations for program evaluation, epidemiology, public health, and health care. Our work spans R, Python, REDCap, Tableau, Power BI, and modern dashboarding stacks — and we utilize methodologies stakeholders can defend.
          </p>
          <Link to="/contact" className="btn-red mt-2" data-testid="about-contact-btn">Work with us</Link>
        </div>
      </section>
    </div>
  );
}
